import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { supabase } from "../supabaseClient";
import { useBooking } from "./BookingContext";

// Theme interface with all customizable properties matching Supabase schema exactly
export interface BookingTheme {
  // Brand Colors - matching your Supabase exactly
  primaryColor: string;
  secondaryColor: string;
  backgroundColor: string;
  cardBackgroundColor: string;
  buttonTextColor: string;

  // Typography - matching your Supabase exactly
  fontFamily: string;
  headingFontFamily: string;
  bodyFontFamily: string;
  borderRadius: string;

  // Branding assets
  logoUrl?: string;
  footerLogoUrl?: string;
  emailLogoUrl?: string;
  faviconUrl?: string;

  // Feature toggles - matching your Supabase exactly
  showAvailabilityCalendar: boolean;
  showGuestReviews: boolean;
  showAmenities: boolean;
  showRoomImages: boolean;
  showHotelImages: boolean;
  showPackages: boolean;

  // Localization - matching your Supabase exactly
  defaultLanguage: string;
  supportedLanguages: string[]; // Array of strings, not comma-separated

  // Contact information - matching your Supabase exactly
  contactEmail?: string;
  contactPhone?: string;
  hotelAddress?: string;
  socialMediaLinks?: {
    facebook?: string;
    instagram?: string;
    youtube?: string;
    linkedin?: string;
    twitter?: string;
  };

  // SEO settings - matching your Supabase exactly
  seoTitle?: string;
  seoDescription?: string;

  // Analytics & tracking - matching your Supabase exactly
  googleAnalyticsId?: string;
  googleAdsConversionId?: string;
  facebookPixelId?: string;

  // Booking URL - matching your Supabase exactly
  bookingUrl?: string;
}

// Default theme matching current booking engine design and your Supabase data
const defaultTheme: BookingTheme = {
  primaryColor: "", // Matching your Supabase data
  secondaryColor: "", // Matching your Supabase data
  backgroundColor: "", // Matching your Supabase data
  cardBackgroundColor: "", // Matching your Supabase data
  buttonTextColor: "", // Matching your Supabase data
  fontFamily: "", // Matching your Supabase data
  headingFontFamily: "", // Matching your Supabase data
  bodyFontFamily: "", // Matching your Supabase data
  borderRadius: "1rem", // Matching your Supabase data

  // Feature toggles defaults - matching your Supabase data
  showAvailabilityCalendar: true,
  showGuestReviews: true,
  showAmenities: true,
  showRoomImages: true,
  showHotelImages: true,
  showPackages: true,

  // Localization defaults - matching your Supabase data
  defaultLanguage: "fr",
  supportedLanguages: ["fr", "en"], // Array format
};

interface ThemeContextType {
  theme: BookingTheme;
  isLoading: boolean;
  error: string | null;
}

const ThemeContext = createContext<ThemeContextType>({
  theme: defaultTheme,
  isLoading: false,
  error: null,
});

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { selectedHotel } = useBooking();
  const [theme, setTheme] = useState<BookingTheme>(defaultTheme);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Function to apply theme to CSS variables
  const applyThemeToDocument = useCallback((themeData: BookingTheme) => {
    const root = document.documentElement;

    // Apply CSS variables
    root.style.setProperty("--hotel-primary-color", themeData.primaryColor);
    root.style.setProperty("--hotel-secondary-color", themeData.secondaryColor);
    root.style.setProperty(
      "--hotel-background-color",
      themeData.backgroundColor
    );
    root.style.setProperty(
      "--hotel-card-background-color",
      themeData.cardBackgroundColor
    );
    root.style.setProperty(
      "--hotel-button-text-color",
      themeData.buttonTextColor
    );
    root.style.setProperty("--hotel-font-family", themeData.fontFamily);
    root.style.setProperty(
      "--hotel-heading-font-family",
      themeData.headingFontFamily
    );
    root.style.setProperty(
      "--hotel-body-font-family",
      themeData.bodyFontFamily
    );
    root.style.setProperty("--hotel-border-radius", themeData.borderRadius);

    // Calculate hover states automatically
    const primaryHover = adjustColorBrightness(themeData.primaryColor, -10);
    root.style.setProperty("--hotel-primary-color-hover", primaryHover);

    // Apply favicon if provided
    if (themeData.faviconUrl) {
      let favicon = document.querySelector(
        'link[rel="icon"]'
      ) as HTMLLinkElement;
      if (!favicon) {
        favicon = document.createElement("link");
        favicon.rel = "icon";
        document.head.appendChild(favicon);
      }
      // Use full image URL for favicon
      favicon.href = themeData.faviconUrl.startsWith('http') 
        ? themeData.faviconUrl 
        : `${window.location.origin}${themeData.faviconUrl}`;
    }

    // Apply analytics tracking codes
    if (themeData.googleAnalyticsId) {
      // Initialize Google Analytics
      const script = document.createElement("script");
      script.src = `https://www.googletagmanager.com/gtag/js?id=${themeData.googleAnalyticsId}`;
      script.async = true;
      document.head.appendChild(script);

      const inlineScript = document.createElement("script");
      inlineScript.innerHTML = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', '${themeData.googleAnalyticsId}');
      `;
      document.head.appendChild(inlineScript);
    }

    if (themeData.facebookPixelId) {
      // Initialize Facebook Pixel
      const pixelScript = document.createElement("script");
      pixelScript.innerHTML = `
        !function(f,b,e,v,n,t,s)
        {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)}(window, document,'script',
        'https://connect.facebook.net/en_US/fbevents.js');
        fbq('init', '${themeData.facebookPixelId}');
        fbq('track', 'PageView');
      `;
      document.head.appendChild(pixelScript);
    }
  }, []);

  // Function to adjust color brightness for hover states
  const adjustColorBrightness = (color: string, amount: number): string => {
    const colorWithoutHash = color.replace("#", "");
    const num = parseInt(colorWithoutHash, 16);
    const amt = Math.round(2.55 * amount);
    const R = (num >> 16) + amt;
    const G = ((num >> 8) & 0x00ff) + amt;
    const B = (num & 0x0000ff) + amt;
    return (
      "#" +
      (
        0x1000000 +
        (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
        (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
        (B < 255 ? (B < 1 ? 0 : B) : 255)
      )
        .toString(16)
        .slice(1)
    );
  };

  // Load theme from database
  const loadTheme = useCallback(async () => {
    console.log(
      "🎨 ThemeProvider loadTheme called with selectedHotel:",
      selectedHotel
    );

    if (!selectedHotel) {
      console.log("🎨 No selected hotel, using default theme");
      setTheme(defaultTheme);
      applyThemeToDocument(defaultTheme);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      console.log("🎨 Loading theme for hotel:", selectedHotel.name);

      // Get booking engine config using selectedHotel.id
      const { data: config, error: configError } = await supabase
        .from("booking_engine_config")
        .select("*")
        .eq("hotel_id", selectedHotel.id)
        .single();

      // Get hotel address and contact info from hotels table
      const { data: hotelData, error: hotelError } = await supabase
        .from("hotels")
        .select("address_street, address_city, address_postal_code, address_country, email, phone")
        .eq("id", selectedHotel.id)
        .single();

      let hotelAddress = '';
      if (hotelError) {
        console.warn("⚠️ Error fetching hotel address:", hotelError);
      } else {
        // Combine address components into full address
        hotelAddress = [
          hotelData?.address_street,
          hotelData?.address_city,
          hotelData?.address_postal_code,
          hotelData?.address_country
        ].filter(Boolean).join(', ');
        console.log("🏠 Hotel address components:", hotelData);
        console.log("🏠 Combined address:", hotelAddress);
      }

      if (configError && configError.code !== "PGRST116") {
        console.error("❌ Error loading booking config:", configError);
        // Don't throw, just use defaults
        console.log("ℹ️ Using default theme due to config error");
        setTheme(defaultTheme);
        applyThemeToDocument(defaultTheme);
        setIsLoading(false);
        return;
      }

      let newTheme = { ...defaultTheme };

      if (config) {
        console.log("✅ Booking engine config found:", config);
        
        // Debug logo URLs from database
        console.log('🔍 Logo URLs from DB:', {
          logo_url: config.logo_url,
          footer_logo_url: config.footer_logo_url,
          email_logo_url: config.email_logo_url,
          favicon_url: config.favicon_url
        });

        // Map database fields to theme properties
        newTheme = {
          primaryColor: config.primary_color || defaultTheme.primaryColor,
          secondaryColor: config.secondary_color || defaultTheme.secondaryColor,
          backgroundColor:
            config.background_color || defaultTheme.backgroundColor,
          cardBackgroundColor:
            config.card_background_color || defaultTheme.cardBackgroundColor,
          buttonTextColor:
            config.button_text_color || defaultTheme.buttonTextColor,
          fontFamily: config.font_family || defaultTheme.fontFamily,
          headingFontFamily:
            config.heading_font_family || defaultTheme.headingFontFamily,
          bodyFontFamily:
            config.body_font_family || defaultTheme.bodyFontFamily,
          borderRadius: config.border_radius || defaultTheme.borderRadius,
          logoUrl: config.logo_url,
          footerLogoUrl: config.footer_logo_url,
          emailLogoUrl: config.email_logo_url,
          faviconUrl: config.favicon_url,

          // Feature toggles - matching your Supabase exactly
          showAvailabilityCalendar:
            config.show_availability_calendar ??
            defaultTheme.showAvailabilityCalendar,
          showGuestReviews:
            config.show_guest_reviews ?? defaultTheme.showGuestReviews,
          showAmenities: config.show_amenities ?? defaultTheme.showAmenities,
          showRoomImages:
            config.show_room_images ?? defaultTheme.showRoomImages,
          showHotelImages:
            config.show_hotel_images ?? defaultTheme.showHotelImages,
          showPackages: config.show_packages ?? defaultTheme.showPackages,

          // Localization - matching your Supabase exactly
          defaultLanguage:
            config.default_language || defaultTheme.defaultLanguage,
          supportedLanguages: Array.isArray(config.supported_languages)
            ? config.supported_languages
            : defaultTheme.supportedLanguages,

          // Contact information - from hotels table
          contactEmail: hotelData?.email,
          contactPhone: hotelData?.phone,
          hotelAddress: hotelAddress,
          socialMediaLinks: (() => {
            const links = typeof config.social_media_links === 'string'
              ? JSON.parse(config.social_media_links || '{}')
              : config.social_media_links;
            
            // Ensure URLs have proper protocol
            if (links && typeof links === 'object') {
              Object.keys(links).forEach(key => {
                if (links[key] && !links[key].startsWith('http')) {
                  links[key] = `https://${links[key]}`;
                }
              });
            }
            return links;
          })(),

          // SEO settings - matching your Supabase exactly
          seoTitle: config.seo_title,
          seoDescription: config.seo_description,

          // Analytics & tracking - matching your Supabase exactly
          googleAnalyticsId: config.google_analytics_id,
          googleAdsConversionId: config.google_ads_conversion_id,
          facebookPixelId: config.facebook_pixel_id,

          // Booking URL - matching your Supabase exactly
          bookingUrl: config.booking_url,
        };
      } else {
        console.log("ℹ️ No booking engine config found, using defaults");
      }

      console.log("🎨 Applied theme:", newTheme);
      setTheme(newTheme);
      applyThemeToDocument(newTheme);
    } catch (err) {
      console.error("❌ Theme loading error:", err);
      setError(err instanceof Error ? err.message : "Failed to load theme");
      // Fallback to default theme
      setTheme(defaultTheme);
      applyThemeToDocument(defaultTheme);
    } finally {
      setIsLoading(false);
    }
  }, [selectedHotel]);

  // Load theme when selectedHotel changes
  useEffect(() => {
    loadTheme();
  }, [selectedHotel, loadTheme]);

  // Set up real-time updates
  useEffect(() => {
    console.log(
      "🔄 Setting up real-time updates useEffect, selectedHotel:",
      selectedHotel
    );

    if (!selectedHotel) {
      console.log("🔄 No selectedHotel, skipping real-time setup");
      return;
    }

    const setupRealtimeUpdates = async () => {
      console.log(
        "🔄 setupRealtimeUpdates called for selectedHotel:",
        selectedHotel.name
      );

      // Use selectedHotel.id directly
      const hotelId = selectedHotel.id;

      if (!hotelId) {
        console.error("❌ No hotel ID found for real-time setup");
        return;
      }

      console.log(
        "🔄 Setting up real-time theme updates for hotel ID:",
        hotelId
      );

      const channel = supabase
        .channel("booking-engine-theme-updates")
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: "booking_engine_config",
            filter: `hotel_id=eq.${hotelId}`,
          },
          (payload) => {
            console.log("🎨 Real-time theme update received:", payload);

            if (
              payload.eventType === "UPDATE" ||
              payload.eventType === "INSERT"
            ) {
              const config = payload.new;
              const newTheme: BookingTheme = {
                primaryColor: config.primary_color || defaultTheme.primaryColor,
                secondaryColor:
                  config.secondary_color || defaultTheme.secondaryColor,
                backgroundColor:
                  config.background_color || defaultTheme.backgroundColor,
                cardBackgroundColor:
                  config.card_background_color ||
                  defaultTheme.cardBackgroundColor,
                buttonTextColor:
                  config.button_text_color || defaultTheme.buttonTextColor,
                fontFamily: config.font_family || defaultTheme.fontFamily,
                headingFontFamily:
                  config.heading_font_family || defaultTheme.headingFontFamily,
                bodyFontFamily:
                  config.body_font_family || defaultTheme.bodyFontFamily,
                borderRadius: config.border_radius || defaultTheme.borderRadius,
                logoUrl: config.logo_url,
                footerLogoUrl: config.footer_logo_url,
                emailLogoUrl: config.email_logo_url,
                faviconUrl: config.favicon_url,

                // Feature toggles - matching your Supabase exactly
                showAvailabilityCalendar:
                  config.show_availability_calendar ??
                  defaultTheme.showAvailabilityCalendar,
                showGuestReviews:
                  config.show_guest_reviews ?? defaultTheme.showGuestReviews,
                showAmenities:
                  config.show_amenities ?? defaultTheme.showAmenities,
                showRoomImages:
                  config.show_room_images ?? defaultTheme.showRoomImages,
                showHotelImages:
                  config.show_hotel_images ?? defaultTheme.showHotelImages,
                showPackages: config.show_packages ?? defaultTheme.showPackages,

                // Localization - matching your Supabase exactly
                defaultLanguage:
                  config.default_language || defaultTheme.defaultLanguage,
                supportedLanguages: Array.isArray(config.supported_languages)
                  ? config.supported_languages
                  : defaultTheme.supportedLanguages,

                // Contact information - from hotels table (will be updated separately)
                contactEmail: theme.contactEmail,
                contactPhone: theme.contactPhone,
                hotelAddress: theme.hotelAddress,
                socialMediaLinks: (() => {
                  const links = typeof config.social_media_links === 'string'
                    ? JSON.parse(config.social_media_links || '{}')
                    : config.social_media_links;
                  
                  // Ensure URLs have proper protocol
                  if (links && typeof links === 'object') {
                    Object.keys(links).forEach(key => {
                      if (links[key] && !links[key].startsWith('http')) {
                        links[key] = `https://${links[key]}`;
                      }
                    });
                  }
                  return links;
                })(),

                // SEO settings - matching your Supabase exactly
                seoTitle: config.seo_title,
                seoDescription: config.seo_description,

                // Analytics & tracking - matching your Supabase exactly
                googleAnalyticsId: config.google_analytics_id,
                googleAdsConversionId: config.google_ads_conversion_id,
                facebookPixelId: config.facebook_pixel_id,

                // Booking URL - matching your Supabase exactly
                bookingUrl: config.booking_url,
              };

              console.log("🎨 Applying real-time theme update:", newTheme);
              setTheme(newTheme);
              applyThemeToDocument(newTheme);
            }
          }
        )
        .subscribe((status) => {
          console.log("🔄 Real-time subscription status:", status);
        });

      // Set up real-time updates for hotels table (contact info and address)
      const hotelChannel = supabase
        .channel("hotel-contact-updates")
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: "hotels",
            filter: `id=eq.${hotelId}`,
          },
          (payload) => {
            console.log("🏨 Real-time hotel update received:", payload);

            if (
              payload.eventType === "UPDATE" ||
              payload.eventType === "INSERT"
            ) {
              const hotelData = payload.new;
              
              // Update contact information and address
              const hotelAddress = [
                hotelData?.address_street,
                hotelData?.address_city,
                hotelData?.address_postal_code,
                hotelData?.address_country
              ].filter(Boolean).join(', ');

              setTheme(prevTheme => ({
                ...prevTheme,
                contactEmail: hotelData?.email,
                contactPhone: hotelData?.phone,
                hotelAddress: hotelAddress,
              }));

              console.log("🏨 Updated hotel contact info:", {
                email: hotelData?.email,
                phone: hotelData?.phone,
                address: hotelAddress
              });
            }
          }
        )
        .subscribe((status) => {
          console.log("🔄 Hotel real-time subscription status:", status);
        });

      console.log(
        "✅ Real-time subscriptions set up successfully for hotel ID:",
        hotelId
      );

      return () => {
        console.log("🔄 Cleaning up real-time subscriptions");
        supabase.removeChannel(channel);
        supabase.removeChannel(hotelChannel);
      };
    };

    const cleanup = setupRealtimeUpdates();
    return () => {
      cleanup?.then((cleanupFn) => cleanupFn?.());
    };
  }, [selectedHotel, applyThemeToDocument]);

  return (
    <ThemeContext.Provider value={{ theme, isLoading, error }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
